/**
 * User Profile Page
 * Allows users to manage their personal profile information and security settings
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUpdateProfile, useChangePassword, useDisableUserAccount } from '../../hooks/useUserProfile';
import { useAuthStore } from '../../store/authStore';
import { format } from 'date-fns';
import { useToast } from '../../hooks/useToast';
import NotificationsTab from '../../components/profile/NotificationsTab';
import type { UpdateProfileData, ChangePasswordData, DisableUserAccountData } from '../../types';

type ProfileTab = 'profile' | 'notifications';

// Reusable components
const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white shadow rounded-lg ${className}`}>{children}</div>
);

const CardHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="px-6 py-4 border-b border-gray-200">{children}</div>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg font-semibold text-gray-900">{children}</h2>
);

const CardContent = ({ children }: { children: React.ReactNode }) => (
  <div className="px-6 py-4">{children}</div>
);

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full z-10">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">{title}</h3>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function UserProfilePage() {
  const { t } = useTranslation('profile');
  const toastify = useToast();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const disableUserAccount = useDisableUserAccount();

  // Active tab
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');

  // Profile editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Disable account modal state
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  // Check if user is account owner
  const isAccountOwner = user?.account && user.account.owner_user_id === user.id;

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">{t('loading')}</div>
      </div>
    );
  }

  const handleStartEditName = () => {
    setName(user.name);
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setName('');
    setIsEditingName(false);
  };

  const handleSaveProfileName = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const data: UpdateProfileData = { name: name.trim() };
    updateProfile.mutate(data, {
      onSuccess: () => {
        setIsEditingName(false);
        setName('');
      },
    });
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) return;

    if (newPassword !== confirmPassword) {
      toastify.error(t('change_password.error_mismatch'));
      return;
    }

    const data: ChangePasswordData = {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: confirmPassword,
    };

    changePassword.mutate(data, {
      onSuccess: () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      },
    });
  };

  const handleDisableAccount = (e: React.FormEvent) => {
    e.preventDefault();
    if (!disablePassword.trim()) return;

    const data: DisableUserAccountData = { password: disablePassword };
    disableUserAccount.mutate(data, {
      onSuccess: () => {
        setDisableModalOpen(false);
        setDisablePassword('');
        // Redirect to login after account is disabled
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      },
    });
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('page_title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('page_subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('profile')}
            className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium ${
              activeTab === 'profile'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            {t('tabs.profile')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('notifications')}
            className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium ${
              activeTab === 'notifications'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            {t('tabs.notifications')}
          </button>
        </nav>
      </div>

      {activeTab === 'notifications' && <NotificationsTab />}

      {activeTab === 'profile' && (
      <div className="space-y-6">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t('profile_information.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile_information.name_label')}
                </label>
                {isEditingName ? (
                  <form onSubmit={handleSaveProfileName} className="flex gap-2">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="flex-1 input"
                      placeholder={t('profile_information.name_placeholder')}
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={updateProfile.isPending || !name.trim()}
                      className="btn btn-primary"
                    >
                      {updateProfile.isPending ? t('profile_information.saving') : t('profile_information.save_button')}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEditName}
                      disabled={updateProfile.isPending}
                      className="btn btn-secondary"
                    >
                      {t('profile_information.cancel_button')}
                    </button>
                  </form>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900">{user.name}</span>
                    <button
                      onClick={handleStartEditName}
                      className="text-sm text-indigo-600 hover:text-indigo-500"
                    >
                      {t('profile_information.edit_button')}
                    </button>
                  </div>
                )}
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile_information.email_label')}
                </label>
                <div className="text-gray-900">{user.email}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('profile_information.email_note')}
                </p>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile_information.role_label')}
                </label>
                <div className="text-gray-900">
                  {user.role === 'admin' ? t('profile_information.role_admin') : t('profile_information.role_user')}
                </div>
              </div>

              {/* Account Created */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile_information.member_since_label')}
                </label>
                <div className="text-gray-900">
                  {format(new Date(user.created_at), 'MMMM d, yyyy')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle>{t('change_password.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label htmlFor="current-password" className="label">
                  {t('change_password.current_password_label')}
                </label>
                <input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input"
                  placeholder={t('change_password.current_password_placeholder')}
                  required
                />
              </div>

              <div>
                <label htmlFor="new-password" className="label">
                  {t('change_password.new_password_label')}
                </label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input"
                  placeholder={t('change_password.new_password_placeholder')}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('change_password.password_hint')}
                </p>
              </div>

              <div>
                <label htmlFor="confirm-password" className="label">
                  {t('change_password.confirm_password_label')}
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                  placeholder={t('change_password.confirm_password_placeholder')}
                  required
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={
                    changePassword.isPending ||
                    !currentPassword ||
                    !newPassword ||
                    !confirmPassword
                  }
                  className="btn btn-primary"
                >
                  {changePassword.isPending ? t('change_password.submitting') : t('change_password.submit_button')}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Danger Zone - Only for non-account-owners */}
        {!isAccountOwner && (
          <Card className="border-2 border-red-200">
            <CardHeader>
              <CardTitle>{t('danger_zone.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">
                    {t('danger_zone.disable_title')}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">
                    {t('danger_zone.disable_description')}
                  </p>
                  <button
                    onClick={() => setDisableModalOpen(true)}
                    className="btn btn-danger"
                  >
                    {t('danger_zone.disable_button')}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info for account owners */}
        {isAccountOwner && (
          <Card className="border-2 border-blue-200">
            <CardContent>
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">{t('account_owner_info.title')}</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    {t('account_owner_info.message')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      )}

      {/* Disable Account Modal */}
      <Modal
        isOpen={disableModalOpen}
        onClose={() => {
          setDisableModalOpen(false);
          setDisablePassword('');
        }}
        title={t('disable_modal.title')}
      >
        <form onSubmit={handleDisableAccount}>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              {t('disable_modal.confirmation_text')}
            </p>

            <div>
              <label htmlFor="disable-password" className="label">
                {t('disable_modal.password_label')}
              </label>
              <input
                id="disable-password"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                className="input"
                placeholder={t('disable_modal.password_placeholder')}
                required
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => {
                  setDisableModalOpen(false);
                  setDisablePassword('');
                }}
                disabled={disableUserAccount.isPending}
                className="btn btn-secondary"
              >
                {t('disable_modal.cancel_button')}
              </button>
              <button
                type="submit"
                disabled={disableUserAccount.isPending || !disablePassword.trim()}
                className="btn btn-danger"
              >
                {disableUserAccount.isPending ? t('disable_modal.submitting') : t('disable_modal.submit_button')}
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
